import { ChipProps, Stack } from '@mui/material';
import { MessageType } from '@schooly/api';
import { TagSelect } from '@schooly/style';
import { FC, PropsWithChildren, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { $enum } from 'ts-enum-util';

import { DropdownSelect, DropdownSelectProps } from '../DropdownSelect';
import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { toggleMultipleValueArrayProperty } from '../utils';

const OPTIONS = $enum(MessageType).map((value, key) => ({ value, label: key }));

type MessageTypeExpandedSelectProps = PropsWithChildren<{
  selectedTypes: MessageType[];
  onApply: (v: MessageType[]) => void;
  onClear: () => void;
  onClose: () => void;
}>;

const TYPES = [MessageType.Announcement, MessageType.Thread];

export const MessageTypeExpandedSelect: FC<MessageTypeExpandedSelectProps> = ({
  selectedTypes: initialSelectedTypes,
  onApply,
  onClose,
  onClear,
}) => {
  const [selectedTypes, setSelectedTypes] = useState<MessageType[]>(initialSelectedTypes);

  const toggleTypes = useCallback((v: MessageType[]) => {
    setSelectedTypes((types) => toggleMultipleValueArrayProperty(types, v));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedTypes);
    onClose();
  }, [selectedTypes, onApply, onClose]);

  const renderContent = useCallback(() => {
    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {renderMessageTypeTags({
            types: TYPES,
            tagProps: (type) => ({
              variant: selectedTypes.includes(type) ? 'filled' : undefined,
            }),
            onClick: (type) => toggleTypes([type]),
          })}
        </Stack>
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [handleApply, onClear, toggleTypes, selectedTypes]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedTypes.length > 0}
      renderContent={renderContent}
      onClose={onClose}
      onClear={onClear}
    >
      {renderMessageTypeTags({
        types: selectedTypes,
        tagProps: { size: 'small' },
        onDelete: (type) => toggleTypes([type]),
      })}
    </ExpandedSelect>
  );
};

export type MessageTypeTagSelectProps = {
  type: MessageType;
} & Omit<ChipProps, 'label'>;

type RenderMessageTypeTagsParams = {
  types: MessageType[];
  onDelete?: (v: MessageType) => void;
  onClick?: (v: MessageType) => void;
  tagProps?:
    | Omit<MessageTypeTagSelectProps, 'type'>
    | ((v: MessageType) => Omit<MessageTypeTagSelectProps, 'type'>);
};

export const renderMessageTypeTags = ({
  types,
  onDelete,
  onClick,
  tagProps,
}: RenderMessageTypeTagsParams) => {
  return types.map((v) => {
    const label = OPTIONS.find((o) => o.value === v)?.label;
    const { formatMessage } = useIntl();
    return (
      <TagSelect
        label={label ? formatMessage({ id: `messages-${label}` }) : label}
        key={v}
        onClick={onClick ? () => onClick(v) : undefined}
        onDelete={onDelete ? () => onDelete(v) : undefined}
        {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
      />
    );
  });
};

export const MessageTypeTagSelect: FC<MessageTypeTagSelectProps> = ({ type, ...props }) => {
  const label = OPTIONS.find((o) => o.value === type)?.label;
  const { formatMessage } = useIntl();
  return (
    <TagSelect label={label ? formatMessage({ id: `messages-${label}` }) : label} {...props} />
  );
};

type MessageTypeSelectMultipleProps = PropsWithChildren<{
  selectedTypes: MessageType[];
  onSelectMessageType: (v: MessageType) => void;
}> &
  Omit<DropdownSelectProps, 'children' | 'renderContent'>;

export const MessageTypeSelectMultiple: FC<MessageTypeSelectMultipleProps> = ({
  selectedTypes,
  onSelectMessageType,
  placeholder,
  ...dropdownProps
}) => {
  const renderContent = useCallback(() => {
    return (
      <Stack direction="row" flexWrap="wrap" gap={1} m={1}>
        {renderMessageTypeTags({
          types: TYPES,
          tagProps: (type) => ({
            variant: selectedTypes.includes(type) ? 'filled' : undefined,
          }),
          onClick: onSelectMessageType,
        })}
      </Stack>
    );
  }, [onSelectMessageType, selectedTypes]);

  return (
    <DropdownSelect
      {...dropdownProps}
      placeholder={placeholder}
      hasValues={!!selectedTypes.length}
      renderContent={renderContent}
    >
      {(opened) => (
        <>
          {renderMessageTypeTags({
            types: selectedTypes,
            tagProps: { size: placeholder ? 'small' : undefined },
            onDelete: opened ? onSelectMessageType : undefined,
          })}
        </>
      )}
    </DropdownSelect>
  );
};
